import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AboutUs.css';

const AboutUs = () => {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      q: 'What is Maveshi Bids and how is it different from a regular mandi?',
      a: 'Maveshi Bids is Pakistan\'s first fully online, live auction platform for livestock, horses, machinery and land. Unlike a physical mandi, you don\'t need to transport your animals anywhere. Buyers from across Pakistan and internationally compete in real time to pay the best possible price for your animal. There is no haggling, no middlemen, and no favouritism — just transparent, competitive bidding.'
    },
    {
      q: 'Do I need any special equipment to participate?',
      a: 'Not at all. You just need a smartphone or computer and an internet connection. Our platform works on all devices and browsers. Creating an account is free, and our support team can guide you through the listing or bidding process step by step.'
    },
    {
      q: 'How do I know the animal I\'m bidding on is healthy and as described?',
      a: 'Every listing must include verified photos, health information, breed details, and, where available, a third-party assessment. Sellers are required to provide accurate information, and buyers are protected by our dispute resolution process if a listing is found to be materially misrepresented.'
    },
    {
      q: 'Can buyers from outside Pakistan bid on Maveshi Bids?',
      a: 'Yes. International buyers — including Pakistani diaspora in the Gulf, UK, Europe, and North America — can register and participate in auctions. This is one of the unique advantages of our platform: it opens your livestock to a global buyer base, which typically results in higher sale prices.'
    },
    {
      q: 'What categories can I sell on Maveshi Bids?',
      a: 'You can sell multi-breed livestock (cattle, goats, sheep, buffalo), horses of all breeds, farm machinery and industrial equipment, and agricultural land. We support both public auctions open to all buyers and private invite-only auctions for premium or sensitive lots.'
    },
    {
      q: 'Is there really no preference for family or friends of the seller?',
      a: 'Correct — and this is by design. Our system is purely bid-based. Once a lot is live, every registered buyer has exactly the same chance to win. The seller has no ability to favour a particular buyer. The highest bid always wins, creating a completely level playing field that traditional markets simply cannot offer.'
    }
  ];

  const whyCards = [
    { icon: '🌍', title: 'No Geographical Limits', desc: 'Sell from Peshawar, buy from Karachi. Reach buyers across all four provinces, AJK, Gilgit-Baltistan, and even international buyers — all from your phone or laptop.' },
    { icon: '⛅', title: 'Weather-Proof Auctions', desc: 'Rain, heat, floods, or dust storms — your auction runs on schedule. No more cancelled mandi days or animals stuck in poor conditions due to weather disruptions.' },
    { icon: '⚖️', title: 'True Market Price', desc: 'Competitive live bidding means buyers outbid each other to secure your animal. You always get the maximum the market is willing to pay — guaranteed by the bidding system itself.' },
    { icon: '🤝', title: 'Zero Favouritism', desc: 'Every registered buyer has equal standing. No preference for family, friends, local contacts, or regular customers. The highest bid wins — period. Full stop.' },
    { icon: '📋', title: 'No Waiting Lists', desc: 'Unlike traditional arrangements where buyers wait their turn, Maveshi Bids operates in real time. Every lot is won competitively, with the fastest and highest bid taking the prize.' },
    { icon: '🔒', title: 'Secure & Transparent', desc: 'Full digital audit trails, verified listings, and secure payment processes give both buyers and sellers the confidence they need to transact with complete peace of mind.' }
  ];

  const categoryCards = [
    { emoji: '🐄', title: 'Multi-Breed Livestock', desc: 'Cattle, goats, sheep, buffalo — all breeds, all ages, all across Pakistan.' },
    { emoji: '🐎', title: 'Horses', desc: 'Marwari, Thoroughbred, Desi, sport horses, stallions and mares listed with full assessments.' },
    { emoji: '🚜', title: 'Farm Machinery', desc: 'Tractors, combines, irrigation equipment, generators — private & clearing sales.' },
    { emoji: '🌾', title: 'Agricultural Land', desc: 'Farm land, orchards, and rural properties auctioned with full transparency and documentation.' }
  ];

  const steps = [
    { num: '01', title: 'Register & Verify', desc: 'Create your free account. Sellers and buyers go through a quick verification to ensure a trusted marketplace.' },
    { num: '02', title: 'List Your Animal or Asset', desc: 'Upload photos, health details, breed information, and any relevant assessment. We help you present your listing at its best.' },
    { num: '03', title: 'Live Auction Goes Live', desc: 'Your lot enters a scheduled live auction. Registered buyers from across Pakistan and abroad place competitive bids in real time.' },
    { num: '04', title: 'Highest Bid Wins. Get Paid.', desc: 'The highest bidder secures the lot. Payment is processed securely and the transaction is completed digitally with full records.' }
  ];

  const fairnessItems = [
    { icon: '🎯', title: 'Bid-Based Pricing Only', desc: 'Price is determined exclusively by competitive bidding. No private deals, no off-market arrangements, no pre-agreed prices.' },
    { icon: '👥', title: 'No Preferential Access', desc: 'Family, friends, and regular contacts of the seller receive no advantages. All registered buyers start from the exact same position.' },
    { icon: '🏷️', title: 'Transparent Bidding History', desc: 'Every bid is logged, timestamped, and visible to all participants. The system cannot be gamed — every move is on the record.' },
    { icon: '🌐', title: 'International Buyers Welcome', desc: 'Pakistani diaspora and international buyers from the Gulf, UK, and beyond can participate, expanding the market and increasing your sale price.' }
  ];

  const statCards = [
    { emoji: '🐄', num: 'Multi', label: 'Breed Livestock' },
    { emoji: '🐎', num: 'Horses', label: 'All Breeds & Classes' },
    { emoji: '🚜', num: 'Machinery', label: 'Farm & Industrial' },
    { emoji: '🌾', num: 'Land', label: 'Agricultural Property' }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <header className="hero-about">
        <div className="hero-about-inner">
          <div className="hero-copy">
            <div className="hero-badge">Pakistan's First Online Live Bidding Platform</div>
            <h1>Sell Your <span>Livestock at the Right Price.</span> From Anywhere.</h1>
            <p className="hero-desc">No middlemen. No waiting lists. No favouritism. Maveshi Bids connects sellers and buyers of multi-breed livestock, horses, machinery, and agricultural land across Pakistan and beyond — through transparent, real-time online auctions.</p>
            <div className="hero-btns">
              <button className="btn-primary" onClick={() => navigate('/register')}>Start Bidding Free →</button>
              <button className="btn-outline" onClick={() => navigate('/how-it-works')}>▶ How It Works</button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="stat-cards-grid">
              {statCards.map((card, idx) => (
                <div key={idx} className="stat-card">
                  <div className="stat-icon">{card.emoji}</div>
                  <div className="stat-num">{card.num}</div>
                  <div className="stat-label">{card.label}</div>
                </div>
              ))}
            </div>
            <div className="live-banner">
              <div className="live-dot"></div>
              <span><strong>Live Auctions Running Now</strong> — Bid from anywhere, anytime, on any device</span>
            </div>
          </div>
        </div>
      </header>

      {/* About Mission Section */}
      <section className="about-mission-section">
        <div className="section-inner about-mission-grid">
          <div className="about-illustration">
            <div className="illus-emoji">🏆</div>
            <p className="illus-quote">Every animal deserves to be sold at its <span>true market value</span> — not the price someone's cousin agreed to.</p>
          </div>
          <div className="about-content">
            <span className="section-tag">About Maveshi Bids</span>
            <h2 className="section-title">Transforming How Pakistan Trades Livestock</h2>
            <p>Maveshi Bids was created to solve a problem every livestock seller in Pakistan knows well: <strong>you never truly know if you got the best price</strong>, and the process is too often shaped by who you know, not what your animal is worth.</p>
            <p>We built Pakistan's first fully online, live auction platform where sellers list their livestock, horses, machinery or agricultural land, and <strong>registered buyers from all over Pakistan and abroad compete in real time</strong> to drive prices up — not down.</p>
            <p>Whether you are in Punjab, Sindh, KPK, Balochistan, or AJK, whether there is a monsoon or a heatwave outside, <strong>your sale goes ahead</strong>. Distance, weather, and geography are no longer barriers.</p>
            <div className="mission-box">
              <h4>🎯 Our Mission</h4>
              <p>To give every livestock owner, farmer, and seller in Pakistan equal access to a fair, transparent, and technology-powered marketplace — one bid at a time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Section */}
      <section className="why-section">
        <div className="section-inner">
          <div>
            <span className="section-tag">Why Maveshi Bids</span>
            <h2 className="section-title">The Platform Built Around Fairness</h2>
            <p className="section-sub">Six core reasons why thousands of sellers and buyers across Pakistan are choosing Maveshi Bids over traditional livestock markets.</p>
          </div>
          <div className="why-grid">
            {whyCards.map((card, idx) => (
              <div key={idx} className="why-card">
                <div className="why-icon">{card.icon}</div>
                <h3>{card.title}</h3>
                <p>{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="categories-section">
        <div className="section-inner">
          <div>
            <span className="section-tag">What You Can Buy & Sell</span>
            <h2 className="section-title">One Platform. Every Agricultural Asset.</h2>
            <p className="section-sub">From prized bulls to horse breeds, from combine harvesters to agricultural land — if it matters to Pakistani agriculture, it lives on Maveshi Bids.</p>
          </div>
          <div className="cat-grid">
            {categoryCards.map((cat, idx) => (
              <div key={idx} className="cat-card">
                <div className="cat-emoji">{cat.emoji}</div>
                <h3>{cat.title}</h3>
                <p>{cat.desc}</p>
                <span className="cat-tag">Live Auctions</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-section">
        <div className="section-inner">
          <div style={{ textAlign: 'center' }}>
            <span className="section-tag">Simple Process</span>
            <h2 className="section-title">From Listing to Sale in 4 Steps</h2>
            <p className="section-sub">Whether you are a first-time seller or an experienced trader, our process is designed to be fast, fair, and simple.</p>
          </div>
          <div className="steps-grid">
            {steps.map((step, idx) => (
              <div key={idx} className="step">
                <div className="step-num">{step.num}</div>
                <h4>{step.title}</h4>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fairness Section */}
      <section className="fairness-section">
        <div className="section-inner fairness-grid">
          <div>
            <span className="section-tag">Equal Opportunity</span>
            <h2 className="section-title">Why Buyers Trust Maveshi Bids</h2>
            <p className="section-sub">Our platform is built to remove every unfair advantage that exists in traditional livestock trade.</p>
            <div className="fairness-list">
              {fairnessItems.map((item, idx) => (
                <div key={idx} className="fair-item">
                  <div className="fair-icon-wrap">{item.icon}</div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="fairness-visual">
            <p className="bid-sim-title">🔴 Live Auction — Lot #247 | Sahiwal Bull</p>
            <div className="bid-simulation">
              <div className="bid-row">
                <span className="bidder-name">🇵🇰 Buyer from Lahore</span>
                <span className="bid-amount">₨ 85,000</span>
              </div>
              <div className="bid-row">
                <span className="bidder-name">🇵🇰 Buyer from Multan</span>
                <span className="bid-amount">₨ 92,000</span>
              </div>
              <div className="bid-row">
                <span className="bidder-name">🇦🇪 Buyer from Dubai</span>
                <span className="bid-amount">₨ 1,05,000</span>
              </div>
              <div className="bid-row">
                <span className="bidder-name">🇵🇰 Buyer from Karachi</span>
                <span className="bid-amount">₨ 1,12,000</span>
              </div>
              <div className="bid-row winner">
                <span className="bidder-name">🇵🇰 Buyer from Faisalabad</span>
                <span className="bid-amount">₨ 1,24,000</span>
                <span className="winner-badge">✓ WINNER</span>
              </div>
            </div>
            <p style={{ color: 'rgba(255,255,255,.55)', fontSize: '.78rem', textAlign: 'center', marginTop: '18px' }}>5 buyers. Equal chances. Best price for the seller.</p>
          </div>
        </div>
      </section>

      {/* Auction Types Section */}
      <section className="auction-types-section">
        <div className="section-inner">
          <div>
            <span className="section-tag">Auction Options</span>
            <h2 className="section-title">Public & Private Auctions</h2>
            <p className="section-sub">Whether you want the widest audience or a curated, invite-only bidding environment, Maveshi Bids gives you the choice.</p>
          </div>
          <div className="auction-cards">
            <div className="auction-card public">
              <span className="auction-badge">Most Popular</span>
              <h3>Public Auction</h3>
              <p>Open to all verified buyers on the platform. Maximum reach, maximum competition, maximum sale price.</p>
              <ul className="auction-features">
                <li><span className="check-icon">✓</span> All verified buyers can bid</li>
                <li><span className="check-icon">✓</span> Widest audience across Pakistan & abroad</li>
                <li><span className="check-icon">✓</span> Fully transparent bidding</li>
                <li><span className="check-icon">✓</span> Highest competitive price outcomes</li>
                <li><span className="check-icon">✓</span> Scheduled weekly live sales</li>
              </ul>
            </div>
            <div className="auction-card private">
              <span className="auction-badge">Invite Only</span>
              <h3>Private Auction</h3>
              <p>Seller selects a pre-approved group of buyers. Ideal for premium, stud, or high-value lots where discretion matters.</p>
              <ul className="auction-features">
                <li><span className="check-icon">✓</span> Seller defines the buyer pool</li>
                <li><span className="check-icon">✓</span> Ideal for premium & stud livestock</li>
                <li><span className="check-icon">✓</span> Confidential listing details</li>
                <li><span className="check-icon">✓</span> Still fully competitive bidding within the group</li>
                <li><span className="check-icon">✓</span> Flexible scheduling</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section-about">
        <div className="section-inner">
          <div>
            <span className="section-tag">Frequently Asked Questions</span>
            <h2 className="section-title">Everything You Need to Know</h2>
            <p className="section-sub">Quick answers to the most common questions from sellers and buyers across Pakistan.</p>
          </div>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className={`faq-item ${openFAQ === index ? 'open' : ''}`}>
                <div className="faq-q" onClick={() => toggleFAQ(index)} role="button" tabIndex="0">
                  {faq.q}
                  <div className="faq-arrow">▾</div>
                </div>
                <p className="faq-a">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="cta-banner-about">
        <div>
          <span className="section-tag" style={{ color: '#4096ff' }}>Join Pakistan's Fastest Growing Agricultural Marketplace</span>
          <h2>Ready to Get the <span>Right Price</span> for Your Livestock?</h2>
          <p>Register for free today and list your first animal, horse, machine, or land plot. Your first auction slot is on us.</p>
          <div className="cta-btns">
            <button className="btn-primary" onClick={() => navigate('/register')}>Create Free Account →</button>
            <button className="btn-outline" onClick={() => navigate('/how-it-works')}>Learn How It Works</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
